/* eslint-disable @typescript-eslint/no-explicit-any */
import { Primitive } from 'types';
import { z, ZodObject, ZodRawShape, ZodType, ZodTypeAny } from 'zod';

const baseV = {
  // zod allows '' by default, different from joi for example
  // we also force the min(1) to be the same error of no string type
  // since thats the behavior we want "invalid entry" instead of "invalid length"
  string: ({ allowEmpty = false, error }: { allowEmpty?: boolean; error?: string } = {}) =>
    allowEmpty
      ? z.string(error).trim()
      : z
          .string(error)
          .trim()
          .min(1, error ?? 'invalid_type'),

  enum: <U extends string>(...v: U[]) => z.enum(v as [U, ...U[]]),

  number: () => z.number(),

  boolean: () => z.boolean(),

  undefined: () => z.undefined(),
  null: () => z.null(),

  object: <T extends ZodRawShape>(schema: T) => z.object(schema),

  /**
   * Zod's native `looseObject` makes it { [x: string]: unknown }
   * and that is type restrictive
   */
  looseObject: <T extends ZodRawShape>(schema: T) => z.object(schema).catchall(z.any()),

  /**
   * Convert an v.object() schema to a loose one
   * with the proper Record<string, any> extended typing
   *
   * THIS TYPE NEEDS REVISION
   */
  asLooseObject: <T extends ZodObject>(objSchema: T) => z.object(objSchema.shape).catchall(z.any()),

  array: <T extends ZodTypeAny>(itemSchema: T) => z.array(itemSchema),

  any: () => z.any(),

  oneOf: <T extends readonly [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]>(...params: T) =>
    z.union(params),

  record: <Value extends ZodTypeAny>(valueSchema: Value) => z.record(z.string(), valueSchema),

  literal: <T extends Primitive>(t: T) => z.literal(t),

  conditional: {
    /**
     * Create a union based on a reference prop:
     * Eg: Media = { id: string, type: 'IMAGE' | 'VIDEO', dimensions: {...}, length: number }
     * ===> Instead do:
     * ```ts
     * const base = V.object({ id: V.string(), dimensions: V.object({...}) })
     *
     * const typeProps = V.conditional.unionByProp('type', [
     *   { type: V.enum('IMAGE') }
     *   { type: V.enum('VIDEO'), length: V.number().min(0) }
     * ])
     *
     * const media = base.and(typeProps)
     * ```
     */
    unionByProp: z.discriminatedUnion,
  },

  convert: {
    number: () => z.coerce.number(),

    boolean: () =>
      z.preprocess((val) => {
        if (typeof val === 'string') {
          if (val.toLowerCase() === 'true') return true;
          if (val.toLowerCase() === 'false') return false;
        }
        return val;
      }, z.boolean()),
  },

  iso: {
    date: z.iso.date,
  },
};

const common = {
  /**
   * Zodv4 removed the simply z.string().trim().email() use case....
   */
  email: () =>
    baseV.string({ error: 'invalid-email' }).toLowerCase().pipe(z.email('invalid-email')),

  uuid: z.uuid,

  decimalString: () => {
    const regex = /^\d+(\.\d{1,2})?$/;

    return baseV.string().regex(regex);
  },
};

export const v = {
  ...baseV,

  common,
};

export type AppSchema = typeof v;

export type PossibleSchema = ZodType<any, any, any>;
